/**
 * GameModeSelector Component
 * Toggle between Free Play and Weekend Competition mode
 * Uses StyleSheet to avoid NativeWind context issues
 */

import React from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { SessionMode } from "../types";

interface GameModeSelectorProps {
  /** Current selected mode */
  mode: SessionMode;
  /** Callback when mode changes */
  onModeChange: (mode: SessionMode) => void;
  /** Whether weekend mode is available (pass from parent to avoid context issues) */
  weekendAvailable?: boolean;
  /** Custom style */
  style?: object;
  /** Compact variant */
  compact?: boolean;
  /** Reason why weekend is unavailable (optional) */
  weekendUnavailableReason?: string;
  /** Whether dark mode is active */
  isDark?: boolean;
}

/**
 * Component to select between Free Play and Weekend Competition mode
 */
export const GameModeSelector: React.FC<GameModeSelectorProps> = ({
  mode,
  onModeChange,
  weekendAvailable = false,
  style = {},
  compact = false,
  weekendUnavailableReason = "Geen actief weekend",
  isDark = true,
}) => {
  const isWeekendModeAvailable = weekendAvailable;

  // Theme-based colors
  const colors = {
    containerBg: isDark ? "#1F2937" : "#F3F4F6",
    optionBg: isDark ? "#374151" : "#FFFFFF",
    textPrimary: isDark ? "#E5E7EB" : "#1F2937",
    textSecondary: isDark ? "#9CA3AF" : "#6B7280",
    border: isDark ? "#374151" : "#E5E7EB",
    iconBg: isDark ? "#374151" : "#E5E7EB",
  };

  if (compact) {
    return (
      <View style={[styles.compactContainer, { backgroundColor: colors.containerBg }, style]}>
        {/* Free Play */}
        <TouchableOpacity
          onPress={() => onModeChange("free")}
          style={[
            styles.compactOption,
            mode === "free" && { backgroundColor: colors.optionBg },
          ]}
          accessibilityLabel="Vrij spelen"
          accessibilityRole="button"
          accessibilityState={{ selected: mode === "free" }}
        >
          <Ionicons
            name="game-controller-outline"
            size={18}
            color={mode === "free" ? "#7C3AED" : colors.textSecondary}
          />
          <Text
            style={[
              styles.compactOptionText,
              { color: colors.textSecondary },
              mode === "free" && styles.compactOptionTextSelected,
            ]}
          >
            Vrij
          </Text>
        </TouchableOpacity>

        {/* Weekend Competition */}
        <TouchableOpacity
          onPress={() => isWeekendModeAvailable && onModeChange("weekend")}
          disabled={!isWeekendModeAvailable}
          style={[
            styles.compactOption,
            mode === "weekend" && { backgroundColor: colors.optionBg },
            !isWeekendModeAvailable && styles.compactOptionDisabled,
          ]}
          accessibilityLabel="Weekend competitie"
          accessibilityRole="button"
          accessibilityState={{
            selected: mode === "weekend",
            disabled: !isWeekendModeAvailable,
          }}
        >
          <Ionicons
            name="trophy-outline"
            size={18}
            color={mode === "weekend" ? "#D97706" : colors.textSecondary}
          />
          <Text
            style={[
              styles.compactOptionText,
              { color: colors.textSecondary },
              mode === "weekend" && styles.compactOptionTextWeekend,
            ]}
          >
            Weekend
          </Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={style}>
      <Text style={[styles.label, { color: colors.textSecondary }]}>Spelmodus</Text>

      <View style={styles.optionsContainer}>
        {/* Free Play Option */}
        <TouchableOpacity
          onPress={() => onModeChange("free")}
          style={[
            styles.option,
            { backgroundColor: colors.containerBg, borderColor: colors.border },
            mode === "free" && styles.optionSelectedFree,
          ]}
          accessibilityLabel="Vrij spelen - voor de lol"
          accessibilityRole="radio"
          accessibilityState={{ checked: mode === "free" }}
        >
          <View
            style={[
              styles.optionIcon,
              { backgroundColor: colors.iconBg },
              mode === "free" && styles.optionIconSelectedFree,
            ]}
          >
            <Ionicons
              name="game-controller"
              size={22}
              color={mode === "free" ? "#FFFFFF" : colors.textSecondary}
            />
          </View>
          <View style={styles.optionContent}>
            <Text
              style={[
                styles.optionTitle,
                { color: colors.textPrimary },
                mode === "free" && styles.optionTitleSelectedFree,
              ]}
            >
              Vrij Spelen
            </Text>
            <Text style={[styles.optionSubtitle, { color: colors.textSecondary }]}>
              Voor de lol, stats worden opgeslagen
            </Text>
          </View>
          {mode === "free" && (
            <Ionicons name="checkmark-circle" size={24} color="#7C3AED" />
          )}
        </TouchableOpacity>

        {/* Weekend Competition Option */}
        <TouchableOpacity
          onPress={() => isWeekendModeAvailable && onModeChange("weekend")}
          disabled={!isWeekendModeAvailable}
          style={[
            styles.option,
            { backgroundColor: colors.containerBg, borderColor: colors.border },
            mode === "weekend" && styles.optionSelectedWeekend,
            !isWeekendModeAvailable && styles.optionDisabled,
          ]}
          accessibilityLabel="Weekend competitie - verdien punten voor je team"
          accessibilityRole="radio"
          accessibilityState={{
            checked: mode === "weekend",
            disabled: !isWeekendModeAvailable,
          }}
        >
          <View
            style={[
              styles.optionIcon,
              { backgroundColor: colors.iconBg },
              mode === "weekend" && styles.optionIconSelectedWeekend,
            ]}
          >
            <Ionicons
              name="trophy"
              size={22}
              color={mode === "weekend" ? "#FFFFFF" : colors.textSecondary}
            />
          </View>
          <View style={styles.optionContent}>
            <Text
              style={[
                styles.optionTitle,
                { color: colors.textPrimary },
                mode === "weekend" && styles.optionTitleSelectedWeekend,
              ]}
            >
              Weekend Competitie
            </Text>
            <Text style={[styles.optionSubtitle, { color: colors.textSecondary }]}>
              {isWeekendModeAvailable
                ? "Verdien punten voor je team!"
                : weekendUnavailableReason}
            </Text>
          </View>
          {mode === "weekend" && (
            <Ionicons name="checkmark-circle" size={24} color="#D97706" />
          )}
        </TouchableOpacity>
      </View>

      {/* Info text */}
      <View style={styles.infoContainer}>
        <Ionicons
          name="information-circle-outline"
          size={16}
          color={colors.textSecondary}
          style={{ marginTop: 1 }}
        />
        <Text style={[styles.infoText, { color: colors.textSecondary }]}>
          {mode === "weekend"
            ? "Punten tellen mee voor het weekend klassement"
            : "Je stats worden opgeslagen in de all-time ranglijst"}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  // Compact styles
  compactContainer: {
    flexDirection: "row",
    borderRadius: 12,
    padding: 4,
  },
  compactOption: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  compactOptionDisabled: {
    opacity: 0.5,
  },
  compactOptionText: {
    marginLeft: 6,
    fontWeight: "500",
    fontSize: 14,
  },
  compactOptionTextSelected: {
    color: "#7C3AED",
  },
  compactOptionTextWeekend: {
    color: "#D97706",
  },

  // Full styles
  label: {
    fontSize: 14,
    fontWeight: "500",
    marginBottom: 8,
  },
  optionsContainer: {
    gap: 8,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
  },
  optionSelectedFree: {
    borderColor: "#7C3AED",
    backgroundColor: "rgba(124, 58, 237, 0.1)",
  },
  optionSelectedWeekend: {
    borderColor: "#D97706",
    backgroundColor: "rgba(217, 119, 6, 0.1)",
  },
  optionDisabled: {
    opacity: 0.6,
  },
  optionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  optionIconSelectedFree: {
    backgroundColor: "#7C3AED",
  },
  optionIconSelectedWeekend: {
    backgroundColor: "#D97706",
  },
  optionContent: {
    flex: 1,
    marginLeft: 12,
  },
  optionTitle: {
    fontWeight: "600",
  },
  optionTitleSelectedFree: {
    color: "#7C3AED",
  },
  optionTitleSelectedWeekend: {
    color: "#D97706",
  },
  optionSubtitle: {
    fontSize: 14,
    marginTop: 2,
  },
  infoContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginTop: 12,
  },
  infoText: {
    flex: 1,
    marginLeft: 6,
    fontSize: 12,
  },
});

export default GameModeSelector;
